import { useState } from "react";
import { Sparkles, Plus, Trash2, RefreshCw, ArrowRight, Download } from "lucide-react";
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  PolarRadiusAxis,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";

const DIMS = ["subject_line", "hook", "body", "cta", "spam_risk", "personalization"];

// Stable per-row id so React's reconciler can track step additions, removals,
// and reorders without falling back to index-keyed remounts.
const newUid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

export default function Analyzer() {
  const [steps, setSteps] = useState([{ _uid: newUid(), subject: "", body: "" }]);
  const [persona, setPersona] = useState("");
  const [goal, setGoal] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const updateStep = (i, patch) => {
    const next = [...steps];
    next[i] = { ...next[i], ...patch };
    setSteps(next);
  };
  const addStep = () => setSteps([...steps, { _uid: newUid(), subject: "", body: "" }]);
  const rmStep = (i) =>
    setSteps(steps.length > 1 ? steps.filter((_, idx) => idx !== i) : steps);

  const analyze = async () => {
    if (steps.some((s) => !s.subject.trim() || !s.body.trim())) {
      return toast.error("Fill subject and body for each step");
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/analyzer/analyze", {
        steps,
        target_persona: persona,
        goal,
      });
      setResult(r.data);
      toast.success("Sequence analyzed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  const apply = (i) => {
    if (!result?.steps?.[i]?.rewrite) return;
    const r = result.steps[i].rewrite;
    updateStep(i, { subject: r.subject, body: r.body });
    toast.success("Rewrite applied");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Premium · Sonnet 4.5
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2 inline-flex items-center gap-3">
            AI Sequence Analyzer
            <Sparkles
              className="w-6 h-6 text-white"
              strokeWidth={1.5}
              data-testid="analyzer-sparkle"
            />
          </h1>
        </div>
        <button
          onClick={analyze}
          disabled={busy}
          data-testid="analyze-button"
          className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-neutral-200 disabled:opacity-50"
        >
          {busy ? (
            <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.8} />
          ) : (
            <Sparkles className="w-4 h-4" strokeWidth={1.8} />
          )}
          {busy ? "Analyzing…" : "Analyze sequence"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <input
          value={persona}
          onChange={(e) => setPersona(e.target.value)}
          placeholder="Target persona - e.g. Series A SaaS CTOs"
          data-testid="persona-input"
          className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-md px-3 py-2 text-sm"
        />
        <input
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Goal - book a 15-min demo"
          data-testid="goal-input"
          className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-md px-3 py-2 text-sm"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* INPUT pane */}
        <div className="space-y-3">
          {steps.map((s, i) => (
            <div
              key={s._uid}
              className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 space-y-2"
              data-testid={`analyzer-step-${i + 1}`}
            >
              <div className="flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
                <span>Step {i + 1}</span>
                <button
                  onClick={() => rmStep(i)}
                  className="p-1 hover:text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
              <input
                value={s.subject}
                onChange={(e) => updateStep(i, { subject: e.target.value })}
                placeholder="Subject"
                className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm"
                data-testid={`analyzer-subject-${i + 1}`}
              />
              <textarea
                value={s.body}
                onChange={(e) => updateStep(i, { body: e.target.value })}
                rows={8}
                placeholder="Email body…"
                className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono leading-relaxed"
                data-testid={`analyzer-body-${i + 1}`}
              />
            </div>
          ))}
          <button
            onClick={addStep}
            data-testid="analyzer-add-step"
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#27272a] rounded-md text-sm text-neutral-400 hover:bg-[#0d0d0d]"
          >
            <Plus className="w-4 h-4" strokeWidth={1.8} /> Add step
          </button>
        </div>

        {/* RESULT pane */}
        <div
          className="space-y-3 lg:sticky lg:top-24 self-start"
          style={{ filter: result ? "none" : "saturate(0.9)" }}
        >
          {!result ? (
            <div
              className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-10 text-center text-neutral-500 text-sm"
              data-testid="analyzer-placeholder"
            >
              Paste your sequence on the left and hit Analyze. You'll get
              per-step scores, a rewrite, and an overall verdict.
            </div>
          ) : (
            <>
              <div
                className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6"
                style={{ boxShadow: "0 0 25px rgba(255,255,255,0.04)" }}
                data-testid="analyzer-overall"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
                      Overall score
                    </div>
                    <div className="font-display text-5xl font-semibold mt-2">
                      {result.overall_score}
                      <span className="text-neutral-500 text-2xl">/100</span>
                    </div>
                  </div>
                  <Sparkles className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-neutral-300 mt-4 leading-relaxed">
                  {result.summary}
                </p>
                {result.report_id && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/analyzer/reports/${result.report_id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="download-pdf-button"
                    className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-white border border-[#27272a] rounded-md px-3 py-1.5 hover:bg-[#161616]"
                  >
                    <Download className="w-3 h-3" strokeWidth={1.8} />
                    Download PDF
                  </a>
                )}
              </div>

              {result.steps.map((sa, i) => (
                <div
                  key={sa.step_number}
                  className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 space-y-3"
                  data-testid={`analyzer-result-${sa.step_number}`}
                >
                  <div className="flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
                    <span>Step {sa.step_number}</span>
                    <button
                      onClick={() => apply(i)}
                      data-testid={`apply-rewrite-${sa.step_number}`}
                      className="text-white inline-flex items-center gap-1 hover:underline"
                    >
                      Apply rewrite
                      <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
                    </button>
                  </div>
                  <div className="h-44">
                    <ResponsiveContainer>
                      <RadarChart
                        data={DIMS.map((d) => ({
                          dim: d.replace("_", " "),
                          v: sa.scores[d] || 0,
                        }))}
                      >
                        <PolarGrid stroke="#262626" />
                        <PolarAngleAxis
                          dataKey="dim"
                          tick={{
                            fill: "#a1a1aa",
                            fontSize: 10,
                            fontFamily: "JetBrains Mono",
                          }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, 100]}
                          tick={false}
                          axisLine={false}
                        />
                        <Radar
                          dataKey="v"
                          stroke="#fafafa"
                          fill="#fafafa"
                          fillOpacity={0.15}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-neutral-300 leading-relaxed">
                    {sa.feedback}
                  </p>
                  <div className="border border-[#1f1f1f] bg-[#0a0a0a] rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-2">
                      Rewrite
                    </div>
                    <div className="text-sm text-white">{sa.rewrite.subject}</div>
                    <pre className="text-xs text-neutral-400 font-mono whitespace-pre-wrap mt-2 leading-relaxed">
                      {sa.rewrite.body}
                    </pre>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
